import React, { useState, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, FormControl, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { Form, FormInput, FormButton, Button, Input } from '../../../Common';
import { useFormContext } from 'react-hook-form';
import { categoryService } from '../../../../services';
import { adminImageService} from '../../../../services/adminImageService';
import { toast } from 'react-toastify';
import type { CategoryLocale } from '../../../../types/category.types';
import './AddCategoryForm.css';

interface AddCategoryFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface Type {
  id: string;
  name: string;
  items: string[];
  itemInput: string;
  isTypeNameFocused: boolean;
  isItemInputFocused: boolean;
  iconURL?: string;
  previewImage?: string;
  uploadingImage?: boolean;
  // Per-language localizations for this type (name + items)
  locale?: {
    [lang: string]: {
      name?: string;
      items?: string[];
    };
  };
}

interface Subcategory {
  id: string;
  name: string;
  types: Type[];
  isNameFocused: boolean;
  iconURL?: string;
  previewImage?: string;
  uploadingImage?: boolean;
  // Per-language localizations for this subcategory name
  locale?: {
    [lang: string]: {
      name?: string;
    };
  };
}

const CATEGORY_SUPPORTED_LOCALES: Record<string, string> = {
  en: 'English',
  ar: 'Arabic',
};

const ImageUploadBox: React.FC<{
  previewImage?: string;
  uploadedImage?: string;
  onImageSelect: (file: File) => void;
  uploading?: boolean;
  label: string;
  size?: 'small' | 'medium';
}> = ({ previewImage, uploadedImage, onImageSelect, uploading, label, size = 'medium' }) => {
  const inputId = `image-upload-${Math.random()}`;
  const boxSize = size === 'small' ? '80px' : '100px';
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
        {label}
      </Typography>
      <Box
        onClick={() => !uploading && document.getElementById(inputId)?.click()}
        sx={{
          width: boxSize,
          height: boxSize,
          border: '2px dashed #ccc',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': { borderColor: '#67314F' }
        }}
      >
        {uploading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '10px', color: '#999' }}>Uploading...</Typography>
          </Box>
        ) : previewImage || uploadedImage ? (
          <Box
            component="img"
            src={previewImage || uploadedImage}
            alt="Preview"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography sx={{ fontSize: '10px', color: '#999', textAlign: 'center', padding: '4px' }}>
            Click to upload
          </Typography>
        )}
        <input
          id={inputId}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImageSelect(file);
          }}
          style={{ display: 'none' }}
          disabled={uploading}
        />
      </Box>
    </Box>
  );
};

const CategoryNameLocaleFields: React.FC<{
  loading: boolean;
  currentLang: string;
  setCurrentLang: React.Dispatch<React.SetStateAction<string>>;
  locale: CategoryLocale;
  setLocale: React.Dispatch<React.SetStateAction<CategoryLocale>>;
  onBeforeLanguageChange?: (fromLang: string, toLang: string, currentName: string) => void;
}> = ({ loading, currentLang, setCurrentLang, locale, setLocale, onBeforeLanguageChange }) => {
  const { getValues, setValue } = useFormContext<{ name: string }>();

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      const { name } = getValues();
      const trimmedName = name?.trim() ?? '';

      // Let parent snapshot current tree state before we switch language
      if (onBeforeLanguageChange) {
        onBeforeLanguageChange(currentLang, newLang, trimmedName);
      }

      const nextLocale: CategoryLocale = {
        ...locale,
        [currentLang]: {
          name: trimmedName,
        },
      };

      setLocale(nextLocale);
      setCurrentLang(newLang);

      const entry = nextLocale[newLang];
      setValue('name', entry?.name ?? '', { shouldDirty: true });
    },
    [currentLang, locale, setCurrentLang, setLocale, getValues, setValue]
  );

  return (
    <>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select
          value={currentLang}
          onChange={(e) => handleLanguageChange(e.target.value)}
          displayEmpty
          disabled={loading}
          sx={{
            backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            borderRadius: 'var(--palette-light-4-value)',
            fontSize: 'var(--palette-light-font-size-default-value)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--palette-light-border-primary-value-hex)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--palette-light-brand-primary-value-hex)',
              borderWidth: '1px',
            },
          }}
        >
          {Object.entries(CATEGORY_SUPPORTED_LOCALES).map(([code, label]) => (
            <MenuItem key={code} value={code}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormInput
        name="name"
        label="Category title"
        placeholder="Enter category name"
        disabled={loading}
        required
      />
    </>
  );
};

const AddCategoryForm: React.FC<AddCategoryFormProps> = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [currentLang, setCurrentLang] = useState<string>('en');
  const [locale, setLocale] = useState<CategoryLocale>({});
  const nextIdRef = useRef(0);
  const nextTypeId = () => `type-${nextIdRef.current++}`;
  const nextSubId = () => `sub-${nextIdRef.current++}`;
  const [subcategories, setSubcategories] = useState<Subcategory[]>(() => {
    const subId = `sub-${nextIdRef.current++}`;
    const typeId = `type-${nextIdRef.current++}`;
    return [
      {
        id: subId,
        name: '',
        types: [{
          id: typeId,
          name: '',
          items: [],
          itemInput: '',
          isTypeNameFocused: false,
          isItemInputFocused: false,
        }],
        isNameFocused: false,
      },
    ];
  });

  const snapshotAndSwitchTreeLocale = useCallback(
    (fromLang: string, toLang: string) => {
      setSubcategories((prev) => {
        // First, snapshot current visible values into locale[fromLang]
        const withSavedLocales = prev.map((sub) => {
          const subLocale = sub.locale ?? {};
          const fromSubEntry = subLocale[fromLang] ?? {};
          fromSubEntry.name = sub.name.trim();

          const updatedSubLocale = {
            ...subLocale,
            [fromLang]: fromSubEntry,
          };

          const updatedTypes = sub.types.map((type) => {
            const typeLocale = type.locale ?? {};
            const fromTypeEntry = typeLocale[fromLang] ?? {};
            fromTypeEntry.name = type.name.trim();
            fromTypeEntry.items = [...type.items];

            return {
              ...type,
              locale: {
                ...typeLocale,
                [fromLang]: fromTypeEntry,
              },
            };
          });

          return {
            ...sub,
            locale: updatedSubLocale,
            types: updatedTypes,
          };
        });

        // Then, compute the visible values for the target language
        const localizedForTarget = withSavedLocales.map((sub) => {
          const subLocale = sub.locale ?? {};
          const targetSubEntry = subLocale[toLang];
          const newSubName = targetSubEntry?.name ?? '';

          const localizedTypes = sub.types.map((type) => {
            const typeLocale = type.locale ?? {};
            const targetTypeEntry = typeLocale[toLang];

            return {
              ...type,
              name: targetTypeEntry?.name ?? '',
              items: targetTypeEntry?.items ?? [],
              itemInput: '',
            };
          });

          return {
            ...sub,
            name: newSubName,
            types: localizedTypes,
          };
        });

        return localizedForTarget;
      });
    },
    []
  );

  const updateSubcategory = (subcategoryId: string, updates: Partial<Subcategory>) => {
    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId ? { ...sub, ...updates } : sub
      )
    );
  };

  const updateType = (subcategoryId: string, typeId: string, updates: Partial<Type>) => {
    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId
          ? {
              ...sub,
              types: sub.types.map((type) =>
                type.id === typeId ? { ...type, ...updates } : type
              ),
            }
          : sub
      )
    );
  };

  const handleAddItem = (subcategoryId: string, typeId: string) => {
    const subcategory = subcategories.find((s) => s.id === subcategoryId);
    const type = subcategory?.types.find((t) => t.id === typeId);
    if (type && type.itemInput.trim()) {
      updateType(subcategoryId, typeId, {
        items: [...type.items, type.itemInput.trim()],
        itemInput: '',
      });
    }
  };

  const handleRemoveItem = (subcategoryId: string, typeId: string, itemIndex: number) => {
    const subcategory = subcategories.find((s) => s.id === subcategoryId);
    const type = subcategory?.types.find((t) => t.id === typeId);
    if (type) {
      updateType(subcategoryId, typeId, {
        items: type.items.filter((_, index) => index !== itemIndex),
      });
    }
  };

  const handleAddType = (subcategoryId: string) => {
    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId
          ? {
              ...sub,
              types: [
                ...sub.types,
                {
                  id: nextTypeId(),
                  name: '',
                  items: [],
                  itemInput: '',
                  isTypeNameFocused: false,
                  isItemInputFocused: false,
                },
              ],
            }
          : sub
      )
    );
  };

  const handleRemoveType = (subcategoryId: string, typeId: string) => {
    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId
          ? {
              ...sub,
              types: sub.types.filter((type) => type.id !== typeId),
            }
          : sub
      )
    );
  };

  const handleAddSubcategory = () => {
    const newSubcategory: Subcategory = {
      id: nextSubId(),
      name: '',
      types: [{
        id: nextTypeId(),
        name: '',
        items: [],
        itemInput: '',
        isTypeNameFocused: false,
        isItemInputFocused: false,
      }],
      isNameFocused: false,
    };
    setSubcategories([...subcategories, newSubcategory]);
  };

  const handleRemoveSubcategory = (subcategoryId: string) => {
    if (subcategories.length > 1) {
      setSubcategories(subcategories.filter((sub) => sub.id !== subcategoryId));
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Preview image before upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
      try {
        const response = await adminImageService.generateUploadUrl({ fileType: file.type });
        console.log('Upload URL response:', response);
        await adminImageService.uploadToS3(response.data.uploadUrl, file);
        setUploadedImage(response.data.key);
      } catch (error) {
        console.error('Failed to compress image:', error);
        toast.error('Failed to process image');
      }
    }
  };

  const handleSubcategoryImageUpload = async (subcategoryId: string, file: File) => {
    updateSubcategory(subcategoryId, { uploadingImage: true });
    
    const reader = new FileReader();
    reader.onloadend = () => {
      updateSubcategory(subcategoryId, { previewImage: reader.result as string });
    };
    reader.readAsDataURL(file);
    
    try {
      const response = await adminImageService.generateUploadUrl({ 
        fileType: file.type,
        categoryId: 'temp'
      });
      
      await adminImageService.uploadToS3(response.data.uploadUrl, file);
      
      updateSubcategory(subcategoryId, { 
        iconURL: response.data.imageUrl,
        previewImage: undefined,
        uploadingImage: false 
      });
      
      toast.success('Subcategory image uploaded');
    } catch (error) {
      toast.error('Failed to upload subcategory image');
      updateSubcategory(subcategoryId, { 
        uploadingImage: false,
        previewImage: undefined 
      });
    }
  };

  const handleTypeImageUpload = async (
    subcategoryId: string, 
    typeId: string, 
    file: File
  ) => {
    updateType(subcategoryId, typeId, { uploadingImage: true });
    
    const reader = new FileReader();
    reader.onloadend = () => {
      updateType(subcategoryId, typeId, { previewImage: reader.result as string });
    };
    reader.readAsDataURL(file);
    
    try {
      const response = await adminImageService.generateUploadUrl({ 
        fileType: file.type,
        categoryId: 'temp',
        subcategoryId: subcategoryId
      });
      
      await adminImageService.uploadToS3(response.data.uploadUrl, file);
      
      updateType(subcategoryId, typeId, { 
        iconURL: response.data.imageUrl,
        previewImage: undefined,
        uploadingImage: false 
      });
      
      toast.success('Type image uploaded');
    } catch (error) {
      toast.error('Failed to upload type image');
      updateType(subcategoryId, typeId, { 
        uploadingImage: false,
        previewImage: undefined 
      });
    }
  };

  const handleSubmit = async (data: { name: string }) => {
    try {
      setLoading(true);

      // Collect all validation errors
      const validationErrors: string[] = [];
      
      // Validate category name
      if (!data.name.trim()) {
        validationErrors.push('Category name is required');
      }

      // Validate image
      if (!uploadedImage) {
        validationErrors.push('Category image is required');
      }

      // Validate subcategories structure
      subcategories.forEach((sub, subIndex) => {
        // If subcategory has a name, it must have at least one valid type
        if (sub.name.trim()) {
          const validTypes = sub.types.filter(type => type.name.trim() && type.items.length > 0);
          if (validTypes.length === 0) {
            validationErrors.push(`Subcategory "${sub.name}" must have at least one type with items`);
          }
        }
        
        // Check each type
        sub.types.forEach((type, typeIndex) => {
          // If type has a name, it must have at least one item
          if (type.name.trim() && type.items.length === 0) {
            validationErrors.push(`Type "${type.name}" in subcategory "${sub.name || `#${subIndex + 1}`}" must have at least one item`);
          }
          
          // If type has items, it must have a name
          if (type.items.length > 0 && !type.name.trim()) {
            validationErrors.push(`Type in subcategory "${sub.name || `#${subIndex + 1}`}" has items but is missing a type name`);
          }
        });
        
        // If subcategory has types, it must have a name
        if (sub.types.length > 0 && !sub.name.trim()) {
          validationErrors.push(`Subcategory #${subIndex + 1} has types but is missing a subcategory name`);
        }
      });

      // Show all validation errors
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => {
          toast.error(error);
        });
        setLoading(false);
        return;
      }

      let iconURL: string | undefined;

      if (uploadedImage) {
        iconURL = uploadedImage;
      }
      
      const subCategories = subcategories
        .filter(sub => sub.name.trim() && sub.types.some(type => type.items.length > 0))
        .map((sub, index) => ({
          name: sub.name,
          displayOrder: index + 1,
          isActive: true,
          ...(sub.iconURL && { iconURL: sub.iconURL }),
          types: sub.types
            .filter(type => type.name.trim() && type.items.length > 0)
            .map((type, typeIndex) => ({
              name: type.name,
              displayOrder: typeIndex + 1,
              isActive: true,
              description: `${type.name} items`,
              items: type.items, // Ensure all items are sent as an array
              ...(type.iconURL && { iconURL: type.iconURL }),
              ...(type.locale && Object.keys(type.locale).length > 0 ? { locale: type.locale } : {}),
            })),
          ...(sub.locale && Object.keys(sub.locale).length > 0 ? { locale: sub.locale } : {}),
        }));

      console.log('📤 Submitting category data:', JSON.stringify({ subCategories }, null, 2));
      console.log('📦 Items being sent:', subCategories.map(sub => 
        sub.types.map(type => ({ typeName: type.name, items: type.items, itemsCount: type.items.length }))
      ));

      const defaultPlaceholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0Y0RjRGNCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5DYXRlZ29yeTwvdGV4dD48L3N2Zz4=';
      
      const finalLocale: CategoryLocale = {
        ...locale,
        [currentLang]: {
          name: data.name.trim(),
        },
      };

      // Default (top-level) category name should be English; other locales only live in locale object
      const defaultName =
        finalLocale.en?.name ?? (currentLang === 'en' ? data.name.trim() : data.name.trim());

      const categoryData: any = {
        name: defaultName,
        iconURL: iconURL || defaultPlaceholder,
        displayOrder: 1,
        ...(subCategories.length > 0 && { subCategories }),
        ...(Object.keys(finalLocale).length > 0 && { locale: finalLocale }),
      };

      await categoryService.createCategory(categoryData);
      
      if (onSuccess) {
         toast.success('Category created successfully');
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to create category:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Failed to create category';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Form<{ name: string }>
      onSubmit={handleSubmit}
      defaultValues={{ name: '' }}
      resetOnSubmit={false}
    >
      <Box className="add-category-form-content">
        {/* Image Upload */}
        <Box
          className="category-image-upload"
          onClick={() => document.getElementById('category-image-upload')?.click()}
        >
          {previewImage ? (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : uploadedImage ? (
            <Box
              component="img"
              src={uploadedImage}
              alt="Uploaded"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <>
              <Box sx={{ width: '96px', height: '96px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M42.41 42.41C38.91 45.91 34.09 48 28.5 48H19.5C13.91 48 9.09 45.91 5.59 42.41C2.09 38.91 0 34.09 0 28.5V19.5C0 13.91 2.09 9.09 5.59 5.59C9.09 2.09 13.91 0 19.5 0H28.5C34.09 0 38.91 2.09 42.41 5.59C45.91 9.09 48 13.91 48 19.5V28.5C48 34.09 45.91 38.91 42.41 42.41Z" stroke="#181818" strokeWidth="2"/>
                  <path d="M20.4 27.76L14.02 13.88L7.5 24.16" stroke="#181818" strokeWidth="1.2"/>
                  <circle cx="18.5" cy="18.5" r="3.5" stroke="#181818" strokeWidth="1.2"/>
                </svg>
              </Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', textAlign: 'center' }}>
                Click or drag image file<br />to this area to upload
              </Typography>
            </>
          )}
          <input
            id="category-image-upload"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </Box>

        {/* Category Name Input with locale support */}
        <CategoryNameLocaleFields
          loading={loading}
          currentLang={currentLang}
          setCurrentLang={setCurrentLang}
          locale={locale}
          setLocale={setLocale}
          onBeforeLanguageChange={(fromLang, toLang) => {
            snapshotAndSwitchTreeLocale(fromLang, toLang);
          }}
        />

        {/* Subcategories, Types, and Items Section */}
        <Box className="tags-section">
          <Typography className="tags-section-title">
            Subcategories, types, and items
          </Typography>
          
          {subcategories.map((subcategory) => (
            <Box key={subcategory.id} className="tag-group">
              <Box className="tag-group-header" sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Input
                    label="Subcategory"
                    placeholder="Enter subcategory name"
                    value={subcategory.name}
                    onChange={(e) => updateSubcategory(subcategory.id, { name: e.target.value })}
                    onFocus={() => updateSubcategory(subcategory.id, { isNameFocused: true })}
                    onBlur={() => updateSubcategory(subcategory.id, { isNameFocused: false })}
                    fullWidth
                    disabled={loading}
                    required
                  />
                </Box>
                
                <ImageUploadBox
                  previewImage={subcategory.previewImage}
                  uploadedImage={subcategory.iconURL}
                  onImageSelect={(file) => handleSubcategoryImageUpload(subcategory.id, file)}
                  uploading={subcategory.uploadingImage}
                  label="Image (optional)"
                  size="small"
                />
                
                <IconButton
                  onClick={() => handleRemoveSubcategory(subcategory.id)}
                  disabled={subcategories.length === 1 || loading}
                  className="delete-group-button"
                >
                  <DeleteIcon sx={{ fontSize: '24px' }} />
                </IconButton>
              </Box>

              {/* Types */}
              {subcategory.types.map((type) => (
                <Box key={type.id} sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--palette-light-2-value)' }}>
                    <Box sx={{ flex: 1 }}>
                      <Input
                        label="Type"
                        placeholder="Enter type name"
                        value={type.name}
                        onChange={(e) => updateType(subcategory.id, type.id, { name: e.target.value })}
                        onFocus={() => updateType(subcategory.id, type.id, { isTypeNameFocused: true })}
                        onBlur={() => updateType(subcategory.id, type.id, { isTypeNameFocused: false })}
                        fullWidth
                        disabled={loading}
                        required
                      />
                    </Box>
                    
                    <ImageUploadBox
                      previewImage={type.previewImage}
                      uploadedImage={type.iconURL}
                      onImageSelect={(file) => handleTypeImageUpload(subcategory.id, type.id, file)}
                      uploading={type.uploadingImage}
                      label="Image (optional)"
                      size="small"
                    />
                    
                    {subcategory.types.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveType(subcategory.id, type.id)}
                        disabled={loading}
                        size="small"
                        sx={{ color: 'var(--palette-light-status-danger-value-hex)' }}
                      >
                        <DeleteIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    )}
                  </Box>

                  <Box className="tag-input-row" sx={{ alignItems: 'flex-end' }}>
                    <Box sx={{ flex: 1 }}>
                      <Input
                        label="Item"
                        placeholder="Enter item name"
                        value={type.itemInput}
                        onChange={(e) => updateType(subcategory.id, type.id, { itemInput: e.target.value })}
                        onFocus={() => updateType(subcategory.id, type.id, { isItemInputFocused: true })}
                        onBlur={() => updateType(subcategory.id, type.id, { isItemInputFocused: false })}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddItem(subcategory.id, type.id);
                          }
                        }}
                        fullWidth
                        disabled={loading}
                        required
                      />
                    </Box>
                    <Button
                      primary
                      size="content"
                      disabled={!type.itemInput || loading}
                      onClick={() => handleAddItem(subcategory.id, type.id)}
                      sx={{
                        height: '100%',
                        minWidth: 'fit-content',
                        width: 'fit-content',
                        flexShrink: 0,
                      }}
                    >
                      Add ⏎
                    </Button>
                  </Box>

                  {type.items.length > 0 && (
                    <Box className="tags-list">
                      {type.items.map((item, itemIndex) => (
                        <Box key={itemIndex} className="tag-chip">
                          <Typography>{item}</Typography>
                          <IconButton
                            onClick={() => handleRemoveItem(subcategory.id, type.id, itemIndex)}
                            disabled={loading}
                            size="small"
                          >
                            <CloseIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}

              <Button
                primary={false}
                bordered
                onClick={() => handleAddType(subcategory.id)}
                disabled={loading}
                sx={{ marginTop: 'var(--palette-light-2-value)' }}
              >
                Add Type
              </Button>
            </Box>
          ))}

          <Button
            primary={false}
            bordered
            onClick={handleAddSubcategory}
            disabled={loading}
          >
            Add Subcategory
          </Button>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box className="add-category-form-actions">
        <FormButton
          type="submit"
          primary
          disabled={loading}
          size="full"
        >
          {loading ? 'Creating...' : 'Add Category'}
        </FormButton>
        <FormButton
          type="button"
          primary={false}
          bordered={false}
          onClick={onCancel}
          size="full"
          disabled={loading}
        >
          Cancel
        </FormButton>
      </Box>
    </Form>
  );
};

export default AddCategoryForm;

